import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import axios from 'axios';
import { useAuth } from './AuthContext';
import type { TextRecord } from '../i18n/stored';

interface WhatsAppNotification {
  id: string;
  type: 'whatsapp_message' | 'whatsapp_status' | 'social_message';
  category?: 'chat' | 'support' | 'assignment' | 'session' | 'assistant' | 'wacloud' | 'social';
  from?: string;
  senderName?: string;
  sessionName?: string;
  sessionStatus?: string;
  message?: string;
  messageType?: string;
  timestamp: string;
  businessPhoneId?: string;
  displayPhoneNumber?: string;
  status?: string;
  recipientId?: string;
  chatId?: string;
  ticketId?: number;
  /** Instagram / Messenger conversation */
  threadId?: number;
  /** Instagram / Facebook comment */
  commentId?: number;
  /** WhatsApp Business conversation */
  conversationId?: number;
  /** Group tracking work item */
  workItemId?: number;
  ticketAction?: string;
  read?: boolean;
  /** Language-neutral title / message (rendered with storedText); absent on older records */
  i18n?: { title?: TextRecord; message?: TextRecord };
  // DB fields
  title?: string;
  data?: any;
  created_at?: string;
}

interface NotificationContextType {
  notifications: WhatsAppNotification[];
  unreadCount: number;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearNotifications: () => void;
  socket: Socket | null;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<WhatsAppNotification[]>([]);
  const [socket, setSocket] = useState<Socket | null>(null);
  // The socket belongs to the signed-in user: it opens after login and is rebuilt when the user changes
  const { user: authUser } = useAuth();
  const authUserId = authUser?.id;

  // Load notifications from DB on mount + cleanup old localStorage
  useEffect(() => {
    // Remove legacy localStorage notifications
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user.id) {
      localStorage.removeItem(`whatsapp_notifications_${user.id}`);
      localStorage.removeItem('whatsapp_notifications');
    }

    const loadFromDB = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      try {
        const res = await axios.get('/api/notifications/list', { headers: getAuthHeader() });
        if (res.data.success && Array.isArray(res.data.data)) {
          const mapped = res.data.data.map((n: any) => ({
            id: n.id,
            type: n.type,
            category: n.category,
            senderName: n.title,
            message: n.message,
            timestamp: n.created_at,
            read: n.read,
            ...(n.data || {}),
          }));
          setNotifications(mapped);
        }
      } catch {
        // Silent fail — notifications will populate from socket
      }
    };
    loadFromDB();
  }, []);

  // Initialize socket connection
  useEffect(() => {
    if (!authUserId) return; // the server refuses sockets without a signed-in user
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';
    const socketUrl = API_URL.replace('/api', '') || window.location.origin;

    const socketInstance = io(socketUrl, {
      // Read on every (re)connect so a renewed token is used; the server rejects sockets without one
      auth: (cb: (data: object) => void) => cb({ token: localStorage.getItem('token') }),
      transports: ['polling', 'websocket'],
      upgrade: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 3000
    });

    socketInstance.on('connect', () => {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user.id) {
        // The server joins the rooms of the authenticated user; the arguments are ignored
        socketInstance.emit('join', user.id, user.role);
      }
    });

    // Listen for new WhatsApp notifications
    socketInstance.on('new_notification', (notification: WhatsAppNotification) => {
      const newNotification = { ...notification, read: false };
      setNotifications(prev => {
        const exists = prev.some(n => n.id === notification.id || (n.data as any)?.id === notification.id);
        if (exists) return prev;
        return [newNotification, ...prev].slice(0, 100);
      });

      // Notification sound
      if (notification.type === 'whatsapp_message' || notification.type === 'social_message') {
        try {
          const audio = new Audio('/notification.mp3');
          audio.play().catch(() => {});
        } catch {}
      }
    });

    // whatsapp_message event removed — no longer emitted from backend

    socketInstance.on('message_status_update', () => {});

    socketInstance.on('error', (error) => {
      console.error('WebSocket error:', error);
    });

    setSocket(socketInstance);

    return () => {
      socketInstance.disconnect();
      setSocket(null);
    };
  }, [authUserId]);

  const markAsRead = useCallback((id: string) => {
    setNotifications(prev =>
      prev.map(n => n.id === id ? { ...n, read: true } : n)
    );
    // Update in DB
    axios.put(`/api/notifications/${id}/read`, {}, { headers: getAuthHeader() }).catch(() => {});
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications(prev =>
      prev.map(n => ({ ...n, read: true }))
    );
    // Update in DB
    axios.put('/api/notifications/read-all', {}, { headers: getAuthHeader() }).catch(() => {});
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
    // Delete from DB
    axios.delete('/api/notifications/clear', { headers: getAuthHeader() }).catch(() => {});
  }, []);

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        markAsRead,
        markAllAsRead,
        clearNotifications,
        socket
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationContext;
